import type { User } from '~/features/auth/auth.types';
import { apiUrl } from '~/lib/config';
import { setAccessTokenExpiry } from './access-token-expiry';
import { ApiError } from './errors';

/**
 * The single door to epic-hub-backend.
 *
 * Auth is entirely cookie-borne: the backend sets `epic_hub_access_token`
 * (httpOnly, SameSite=lax, path=/) and `epic_hub_refresh_token` (httpOnly,
 * SameSite=strict, path=`${API_PREFIX}/auth`). We never see either from JS, so
 * every request just needs `credentials: 'include'` and the browser does the
 * rest. Nothing here reads, stores, or forwards a token.
 *
 * Rotation has two halves. Proactive: `~/features/auth/token-rotation`
 * refreshes shortly before the 15-minute access token expires, so a user
 * sitting on the dashboard never sees a failed request. Reactive: anything that
 * still comes back 401 — clock skew, a sleeping laptop, a tab from bfcache —
 * triggers one refresh and one replay.
 *
 * Concurrent 401s must share a single refresh call. The backend rotates the
 * refresh token on every use and revokes the whole family if a used token is
 * replayed, so a burst of parallel refreshes looks exactly like token theft and
 * logs the user out.
 */

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** Serialised as JSON unless it is already a BodyInit. */
  body?: unknown;
  searchParams?: Record<string, string | number | boolean | null | undefined>;
  /** Opt out of refresh-and-replay. Set by the refresh call itself. */
  skipAuthRefresh?: boolean;
}

/** Mirrors the backend's AuthResponseDto. */
export interface AuthResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
}

/**
 * A 401 from these is the answer, not a stale-token problem: refreshing and
 * replaying a failed login would be pointless and would burn rate-limit budget
 * (the backend allows 10 requests per 5 minutes per IP on /auth).
 *
 * `/auth/change-password` is in the list for a sharper reason: its 401 means
 * "that is not your current password". Treating it as a stale session would
 * replay the wrong password, and — if the refresh happened to fail — sign the
 * user out for a typo.
 */
const NO_REFRESH_PATHS = [
  '/auth/login',
  '/auth/register',
  '/auth/refresh',
  '/auth/change-password',
];

// --- session-expiry notifications -------------------------------------------

type SessionExpiredListener = () => void;

const sessionExpiredListeners = new Set<SessionExpiredListener>();

/**
 * Fires when refresh itself fails — the refresh token is gone, expired, or was
 * revoked. The app responds by clearing cached data and sending the user to
 * /login. Returns an unsubscribe function.
 */
export function onSessionExpired(listener: SessionExpiredListener): () => void {
  sessionExpiredListeners.add(listener);
  return () => sessionExpiredListeners.delete(listener);
}

function emitSessionExpired(): void {
  for (const listener of sessionExpiredListeners) listener();
}

// --- refresh ----------------------------------------------------------------

let inFlightRefresh: Promise<boolean> | null = null;

/**
 * Rotate the token pair. Safe to call from anywhere and as often as you like:
 * callers that arrive while a refresh is running await that same promise rather
 * than starting a second rotation.
 */
export function refreshSession(): Promise<boolean> {
  inFlightRefresh ??= performRefresh().finally(() => {
    inFlightRefresh = null;
  });

  return inFlightRefresh;
}

async function performRefresh(): Promise<boolean> {
  try {
    // Body is `{}` rather than absent: the DTO's refreshToken is optional and
    // only used by non-browser clients, but ValidationPipe wants a real object.
    const response = await rawRequest('/auth/refresh', { method: 'POST', body: {} });

    if (!response.ok) {
      emitSessionExpired();
      return false;
    }

    const payload = (await response.json()) as AuthResponse;
    setAccessTokenExpiry(payload.expiresIn);
    return true;
  } catch {
    // Network failure, not an auth failure — the session may well still be
    // valid, so do not tear it down. The next request will try again.
    return false;
  }
}

// --- core request -----------------------------------------------------------

function buildUrl(path: string, searchParams: RequestOptions['searchParams']): string {
  const url = apiUrl(path);
  if (!searchParams) return url;

  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(searchParams)) {
    if (value !== undefined && value !== null) query.set(key, String(value));
  }

  const queryString = query.toString();
  return queryString ? `${url}?${queryString}` : url;
}

function isBodyInit(value: unknown): value is BodyInit {
  return (
    typeof value === 'string' ||
    value instanceof FormData ||
    value instanceof Blob ||
    value instanceof ArrayBuffer ||
    value instanceof URLSearchParams ||
    value instanceof ReadableStream
  );
}

async function rawRequest(path: string, options: RequestOptions): Promise<Response> {
  const { body, searchParams, skipAuthRefresh: _skip, headers, ...rest } = options;

  const requestHeaders = new Headers(headers);
  requestHeaders.set('Accept', 'application/json');

  let requestBody: BodyInit | undefined;
  if (body !== undefined) {
    if (isBodyInit(body)) {
      requestBody = body;
    } else {
      requestBody = JSON.stringify(body);
      if (!requestHeaders.has('Content-Type')) {
        requestHeaders.set('Content-Type', 'application/json');
      }
    }
  }

  return fetch(buildUrl(path, searchParams), {
    ...rest,
    headers: requestHeaders,
    body: requestBody,
    // The whole auth scheme rests on this line.
    credentials: 'include',
  });
}

async function parseBody<T>(response: Response): Promise<T> {
  if (response.status === 204 || response.headers.get('Content-Length') === '0') {
    return undefined as T;
  }

  const contentType = response.headers.get('Content-Type') ?? '';
  if (!contentType.includes('application/json')) {
    return (await response.text()) as T;
  }

  return (await response.json()) as T;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const refreshable = !options.skipAuthRefresh && !NO_REFRESH_PATHS.includes(path);

  let response: Response;
  try {
    response = await rawRequest(path, options);
  } catch {
    throw ApiError.network();
  }

  if (response.status === 401 && refreshable) {
    const refreshed = await refreshSession();

    if (refreshed) {
      try {
        response = await rawRequest(path, options);
      } catch {
        throw ApiError.network();
      }
    }
  }

  if (!response.ok) throw await ApiError.fromResponse(response);

  return parseBody<T>(response);
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'GET' }),

  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'POST', body }),

  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'PATCH', body }),

  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'PUT', body }),

  delete: <T>(path: string, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'DELETE' }),
};
